import { decodeWindows1254 } from './bytes-text.js';
import {
  BUILT_IN_DATE_FORMATS,
  excelSerialToText,
  isDateFormatCode,
  renderNumber,
  SHEET_MAX_CELL_CHARS,
  XLSX_MAX_COLUMNS,
} from './xlsx-read.js';

/**
 * The first worksheet of a legacy Excel 97–2003 `.xls` — BIFF8 records inside
 * an OLE2 compound file — as rows of the text a person sees in Excel, rendered
 * by the same rules as `readXlsxFirstSheet`, so one statement profile reads a
 * bank's export whichever of the two containers it arrives in
 * (docs/decisions/0101).
 *
 * **Every number the file states about itself is a claim.** Sector chains are
 * walked with a visited set and bounded by the sectors the file really has; a
 * directory tree is walked the same way; a record's length is checked against
 * the stream before it is read; and rows, cells, strings and records are
 * counted as they are kept. Nothing is evaluated: a formula is read by its
 * cached value, and macros, embedded objects and every other stream are never
 * opened. An encrypted workbook — a `FILEPASS` record, or an `.xlsx` whose
 * password wrapped it in a compound file — is refused by name.
 */

export type XlsLimits = {
  readonly maxFileBytes: number;
  /** One stream — the workbook, the mini stream, the directory — at most. */
  readonly maxStreamBytes: number;
  readonly maxFatSectors: number;
  readonly maxDirectoryEntries: number;
  readonly maxRecords: number;
  readonly maxSharedStrings: number;
  /** Excel's own limit on a cell's text. */
  readonly maxStringChars: number;
  /** Every string read, shared or not, together. */
  readonly maxTotalStringChars: number;
  readonly maxCells: number;
  /**
   * Characters across every cell kept, padding included. A shared string is
   * stored once and may be named by every cell, so this — not the string
   * table — is what bounds the text a sheet becomes.
   */
  readonly maxCellChars: number;
  readonly maxFormats: number;
  readonly maxStyles: number;
  readonly maxSheets: number;
};

export type XlsReadOptions = {
  readonly decimalSeparator: ',' | '.';
  readonly maxRows: number;
  readonly limits?: XlsLimits;
};

export const XLS_REFUSALS = [
  'XLS_NOT_A_COMPOUND_FILE',
  'XLS_TOO_LARGE',
  'XLS_SECTOR_OUT_OF_RANGE',
  'XLS_CHAIN_CYCLE',
  'XLS_TRUNCATED',
  'XLS_MALFORMED',
  'XLS_TOO_MANY_ENTRIES',
  'XLS_ENCRYPTED',
  'XLS_NOT_A_WORKBOOK',
  'XLS_VERSION_UNSUPPORTED',
  'XLS_TOO_MANY_RECORDS',
  'XLS_TOO_MANY_STRINGS',
  'XLS_STRING_TOO_LONG',
  'XLS_TOO_MANY_CELLS',
  'XLS_TOO_MUCH_TEXT',
  'XLS_TOO_MANY_ROWS',
] as const;
export type XlsRefusal = (typeof XLS_REFUSALS)[number];

export class XlsRefusedError extends Error {
  readonly code: XlsRefusal;

  constructor(code: XlsRefusal, message: string) {
    super(message);
    this.name = 'XlsRefusedError';
    this.code = code;
  }
}

/**
 * A statement never needs more. The file is at most the upload's 10 MiB, so no
 * stream inside it can honestly be larger; a real bank export holds a handful
 * of directory entries, a few hundred formats and styles, and one sheet.
 */
export const XLS_LIMITS: XlsLimits = {
  maxFileBytes: 10 * 1024 * 1024,
  maxStreamBytes: 10 * 1024 * 1024,
  maxFatSectors: 1_024,
  maxDirectoryEntries: 1_024,
  maxRecords: 3_000_000,
  maxSharedStrings: 500_000,
  maxStringChars: 32_767,
  maxTotalStringChars: 20_000_000,
  maxCells: 1_000_000,
  maxCellChars: SHEET_MAX_CELL_CHARS,
  maxFormats: 4_096,
  maxStyles: 16_384,
  maxSheets: 1_024,
};

export type XlsSheet = {
  /** `rows[n]` is spreadsheet row `n + 1`; a row with no cell is an empty array. */
  readonly rows: string[][];
};

const SIGNATURE = [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1];
const HEADER_BYTES = 512;
const MAX_REGULAR_SECTOR = 0xfffffffa;
const END_OF_CHAIN = 0xfffffffe;
const FREE_SECTOR = 0xffffffff;
const NO_STREAM = 0xffffffff;
const HEADER_DIFAT_ENTRIES = 109;
const DIRECTORY_ENTRY_BYTES = 128;
const MINI_SECTOR_BYTES = 64;
const MINI_STREAM_CUTOFF = 4096;

const refuse = (code: XlsRefusal, message: string): never => {
  throw new XlsRefusedError(code, message);
};

export function isCompoundFile(bytes: Uint8Array): boolean {
  return (
    bytes.length >= SIGNATURE.length && SIGNATURE.every((byte, index) => bytes[index] === byte)
  );
}

type CompoundStream = { readonly name: string; readonly start: number; readonly size: number };

type CompoundFile = {
  readonly streams: readonly CompoundStream[];
  read(stream: CompoundStream): Uint8Array;
};

/** The compound file's root storage, its top-level streams listed and readable — nothing else. */
function openCompoundFile(bytes: Uint8Array, limits: XlsLimits): CompoundFile {
  if (bytes.length > limits.maxFileBytes)
    refuse('XLS_TOO_LARGE', 'The file is larger than allowed');
  if (bytes.length < HEADER_BYTES || !isCompoundFile(bytes)) {
    refuse('XLS_NOT_A_COMPOUND_FILE', 'The file is not an OLE compound file');
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const u16 = (offset: number) => view.getUint16(offset, true);
  const u32 = (offset: number) => view.getUint32(offset, true);

  const majorVersion = u16(26);
  const sectorShift = u16(30);
  if (u16(28) !== 0xfffe) refuse('XLS_MALFORMED', 'The compound file has no byte-order mark');
  if (!((majorVersion === 3 && sectorShift === 9) || (majorVersion === 4 && sectorShift === 12))) {
    refuse('XLS_MALFORMED', 'The compound file has an unknown version or sector size');
  }
  if (u16(32) !== 6 || u32(56) !== MINI_STREAM_CUTOFF) {
    refuse('XLS_MALFORMED', 'The compound file has an unknown mini sector size or cutoff');
  }
  const sectorBytes = 1 << sectorShift;
  const sectorCount = Math.max(0, Math.ceil((bytes.length - sectorBytes) / sectorBytes));
  const fatSectorCount = u32(44);
  const firstDirectorySector = u32(48);
  const firstMiniFatSector = u32(60);
  const miniFatSectorCount = u32(64);
  const firstDifatSector = u32(68);
  const difatSectorCount = u32(72);
  if (fatSectorCount > limits.maxFatSectors || fatSectorCount > sectorCount) {
    refuse('XLS_MALFORMED', 'The compound file claims more allocation sectors than it holds');
  }
  if (difatSectorCount > sectorCount || miniFatSectorCount > sectorCount) {
    refuse('XLS_MALFORMED', 'The compound file claims more sectors than it holds');
  }

  const sector = (id: number): Uint8Array => {
    if (id > MAX_REGULAR_SECTOR || id >= sectorCount) {
      refuse('XLS_SECTOR_OUT_OF_RANGE', `Sector ${id} is outside the file`);
    }
    const start = (id + 1) * sectorBytes;
    return bytes.subarray(start, Math.min(bytes.length, start + sectorBytes));
  };
  const entriesPerSector = sectorBytes / 4;
  const sectorWord = (data: Uint8Array, index: number): number => {
    if (data.length < (index + 1) * 4) refuse('XLS_TRUNCATED', 'A sector is cut short');
    return new DataView(data.buffer, data.byteOffset, data.byteLength).getUint32(index * 4, true);
  };

  const fatSectorIds: number[] = [];
  for (
    let index = 0;
    index < HEADER_DIFAT_ENTRIES && fatSectorIds.length < fatSectorCount;
    index += 1
  ) {
    fatSectorIds.push(u32(76 + index * 4));
  }
  const visitedDifat = new Set<number>();
  let difatSector = firstDifatSector;
  while (fatSectorIds.length < fatSectorCount) {
    if (difatSector === END_OF_CHAIN || difatSector === FREE_SECTOR) {
      refuse('XLS_TRUNCATED', 'The allocation table index ends before every sector is named');
    }
    if (visitedDifat.has(difatSector)) refuse('XLS_CHAIN_CYCLE', 'The allocation index loops');
    if (visitedDifat.size >= difatSectorCount) {
      refuse('XLS_MALFORMED', 'The allocation index is longer than it claims');
    }
    visitedDifat.add(difatSector);
    const data = sector(difatSector);
    for (
      let index = 0;
      index < entriesPerSector - 1 && fatSectorIds.length < fatSectorCount;
      index += 1
    ) {
      fatSectorIds.push(sectorWord(data, index));
    }
    difatSector = sectorWord(data, entriesPerSector - 1);
  }

  const fat = new Uint32Array(fatSectorCount * entriesPerSector);
  const seenFatSectors = new Set<number>();
  fatSectorIds.forEach((id, position) => {
    if (seenFatSectors.has(id)) refuse('XLS_CHAIN_CYCLE', 'An allocation sector is named twice');
    seenFatSectors.add(id);
    const data = sector(id);
    for (let index = 0; index < entriesPerSector; index += 1) {
      fat[position * entriesPerSector + index] = sectorWord(data, index);
    }
  });

  const chain = (
    table: Uint32Array,
    start: number,
    addressable: number,
    maxLinks: number,
    what: string,
    beyond: 'stop' | 'refuse',
  ): number[] => {
    const ids: number[] = [];
    const visited = new Uint8Array(addressable);
    let current = start;
    while (current !== END_OF_CHAIN) {
      if (ids.length === maxLinks) {
        if (beyond === 'stop') break;
        refuse('XLS_TOO_LARGE', `The ${what} is longer than allowed`);
      }
      if (current > MAX_REGULAR_SECTOR || current >= addressable || current >= table.length) {
        refuse('XLS_SECTOR_OUT_OF_RANGE', `The ${what} names a sector outside the file`);
      }
      if (visited[current] === 1) refuse('XLS_CHAIN_CYCLE', `The ${what} loops`);
      visited[current] = 1;
      ids.push(current);
      current = table[current] as number;
    }
    return ids;
  };

  const readChain = (start: number, size: number, what: string): Uint8Array => {
    if (size > limits.maxStreamBytes) refuse('XLS_TOO_LARGE', `The ${what} is larger than allowed`);
    const needed = Math.ceil(size / sectorBytes);
    const ids = size === 0 ? [] : chain(fat, start, sectorCount, needed, what, 'stop');
    if (ids.length < needed) refuse('XLS_TRUNCATED', `The ${what} ends before its size`);
    const out = new Uint8Array(size);
    let written = 0;
    for (const id of ids.slice(0, needed)) {
      const data = sector(id);
      const take = Math.min(data.length, size - written);
      if (take < Math.min(sectorBytes, size - written)) {
        refuse('XLS_TRUNCATED', `The ${what} runs past the end of the file`);
      }
      out.set(data.subarray(0, take), written);
      written += take;
    }
    return out;
  };

  const maxDirectorySectors = Math.ceil(
    (limits.maxDirectoryEntries * DIRECTORY_ENTRY_BYTES) / sectorBytes,
  );
  const directorySectors = (() => {
    try {
      return chain(
        fat,
        firstDirectorySector,
        sectorCount,
        maxDirectorySectors,
        'directory',
        'refuse',
      );
    } catch (error) {
      if (error instanceof XlsRefusedError && error.code === 'XLS_TOO_LARGE') {
        refuse('XLS_TOO_MANY_ENTRIES', 'The directory holds more entries than allowed');
      }
      throw error;
    }
  })();
  if (directorySectors.length === 0) refuse('XLS_MALFORMED', 'The compound file has no directory');
  const directory = new Uint8Array(directorySectors.length * sectorBytes);
  directorySectors.forEach((id, index) => {
    const data = sector(id);
    if (data.length < sectorBytes) refuse('XLS_TRUNCATED', 'The directory is cut short');
    directory.set(data, index * sectorBytes);
  });
  const directoryView = new DataView(directory.buffer);
  const entryCount = directory.length / DIRECTORY_ENTRY_BYTES;

  type Entry = {
    name: string;
    type: number;
    left: number;
    right: number;
    child: number;
    start: number;
    size: number;
  };
  const entryAt = (index: number): Entry => {
    if (index >= entryCount) refuse('XLS_MALFORMED', 'The directory names an entry it lacks');
    const base = index * DIRECTORY_ENTRY_BYTES;
    const nameBytes = directoryView.getUint16(base + 64, true);
    if (nameBytes > 64 || nameBytes % 2 !== 0)
      refuse('XLS_MALFORMED', 'A directory name is malformed');
    let name = '';
    for (let offset = 0; offset + 2 < nameBytes; offset += 2) {
      name += String.fromCharCode(directoryView.getUint16(base + offset, true));
    }
    const sizeHigh = directoryView.getUint32(base + 124, true);
    if (majorVersion === 4 && sizeHigh !== 0)
      refuse('XLS_TOO_LARGE', 'A stream is larger than allowed');
    return {
      name,
      type: directory[base + 66] as number,
      left: directoryView.getUint32(base + 68, true),
      right: directoryView.getUint32(base + 72, true),
      child: directoryView.getUint32(base + 76, true),
      start: directoryView.getUint32(base + 116, true),
      size: directoryView.getUint32(base + 120, true),
    };
  };

  const root = entryAt(0);
  if (root.type !== 5) refuse('XLS_MALFORMED', 'The directory does not start at its root');

  const streams: CompoundStream[] = [];
  const visitedEntries = new Uint8Array(entryCount);
  visitedEntries[0] = 1;
  const pending = root.child === NO_STREAM ? [] : [root.child];
  while (pending.length > 0) {
    const index = pending.pop() as number;
    if (index >= entryCount) refuse('XLS_MALFORMED', 'The directory names an entry it lacks');
    if (visitedEntries[index] === 1) refuse('XLS_CHAIN_CYCLE', 'The directory tree loops');
    visitedEntries[index] = 1;
    const entry = entryAt(index);
    if (entry.type === 2) streams.push({ name: entry.name, start: entry.start, size: entry.size });
    if (entry.left !== NO_STREAM) pending.push(entry.left);
    if (entry.right !== NO_STREAM) pending.push(entry.right);
  }

  let miniStream: Uint8Array | null = null;
  let miniFat: Uint32Array | null = null;
  const readMini = (stream: CompoundStream): Uint8Array => {
    if (miniStream === null || miniFat === null) {
      miniStream = readChain(root.start, root.size, 'mini stream');
      const miniFatSectors =
        miniFatSectorCount === 0
          ? []
          : chain(
              fat,
              firstMiniFatSector,
              sectorCount,
              miniFatSectorCount,
              'mini allocation table',
              'stop',
            );
      miniFat = new Uint32Array(miniFatSectors.length * entriesPerSector);
      miniFatSectors.forEach((id, position) => {
        const data = sector(id);
        for (let index = 0; index < entriesPerSector; index += 1) {
          (miniFat as Uint32Array)[position * entriesPerSector + index] = sectorWord(data, index);
        }
      });
    }
    const addressable = Math.floor(miniStream.length / MINI_SECTOR_BYTES);
    const needed = Math.ceil(stream.size / MINI_SECTOR_BYTES);
    const ids =
      stream.size === 0
        ? []
        : chain(miniFat, stream.start, addressable, needed, 'mini stream chain', 'stop');
    if (ids.length < needed) refuse('XLS_TRUNCATED', 'A small stream ends before its size');
    const out = new Uint8Array(stream.size);
    ids.slice(0, needed).forEach((id, index) => {
      const offset = id * MINI_SECTOR_BYTES;
      const take = Math.min(MINI_SECTOR_BYTES, stream.size - index * MINI_SECTOR_BYTES);
      out.set(
        (miniStream as Uint8Array).subarray(offset, offset + take),
        index * MINI_SECTOR_BYTES,
      );
    });
    return out;
  };

  return {
    streams,
    read: (stream) =>
      stream.size < MINI_STREAM_CUTOFF
        ? readMini(stream)
        : readChain(stream.start, stream.size, stream.name),
  };
}

const named = (streams: readonly CompoundStream[], name: string): CompoundStream[] =>
  streams.filter((stream) => stream.name.toLowerCase() === name.toLowerCase());

/**
 * Whether a compound file holds a workbook at all — or one an `.xlsx` password
 * wrapped — so a Word document or an installer is told apart from a statement
 * by its bytes. A file the reader cannot open is not one.
 */
export function compoundFileHoldsWorkbook(bytes: Uint8Array): boolean {
  try {
    const { streams } = openCompoundFile(bytes, XLS_LIMITS);
    return ['Workbook', 'Book', 'EncryptedPackage'].some((name) => named(streams, name).length > 0);
  } catch (error) {
    if (error instanceof XlsRefusedError) return false;
    throw error;
  }
}

const RECORD = {
  bof: 0x0809,
  eof: 0x000a,
  continue: 0x003c,
  filePass: 0x002f,
  codePage: 0x0042,
  dateMode: 0x0022,
  format: 0x041e,
  xf: 0x00e0,
  boundSheet: 0x0085,
  sst: 0x00fc,
  labelSst: 0x00fd,
  label: 0x0204,
  rString: 0x00d6,
  number: 0x0203,
  rk: 0x027e,
  mulRk: 0x00bd,
  formula: 0x0006,
  string: 0x0207,
  boolErr: 0x0205,
  blank: 0x0201,
  mulBlank: 0x00be,
} as const;

const BIFF8 = 0x0600;
const MAX_RECORD_BYTES = 8224;
const CODEPAGE_WINDOWS_1254 = 1254;

/** One record with the `CONTINUE` records after it, which carry the rest of a long string. */
type LogicalRecord = { readonly type: number; readonly segments: readonly Uint8Array[] };

type Budget = { records: number; stringChars: number };

class RecordStream {
  private offset: number;

  constructor(
    private readonly bytes: Uint8Array,
    start: number,
    private readonly limits: XlsLimits,
    private readonly budget: Budget,
  ) {
    this.offset = start;
  }

  private header(at: number): { type: number; length: number } {
    if (at + 4 > this.bytes.length) refuse('XLS_TRUNCATED', 'The workbook ends inside a record');
    const type = (this.bytes[at] as number) | ((this.bytes[at + 1] as number) << 8);
    const length = (this.bytes[at + 2] as number) | ((this.bytes[at + 3] as number) << 8);
    if (length > MAX_RECORD_BYTES) refuse('XLS_MALFORMED', 'A record is longer than BIFF8 allows');
    if (at + 4 + length > this.bytes.length)
      refuse('XLS_TRUNCATED', 'A record runs past the workbook');
    return { type, length };
  }

  next(): LogicalRecord {
    const { type, length } = this.header(this.offset);
    const segments = [this.bytes.subarray(this.offset + 4, this.offset + 4 + length)];
    this.offset += 4 + length;
    this.count();
    while (this.offset + 4 <= this.bytes.length) {
      const following = this.header(this.offset);
      if (following.type !== RECORD.continue) break;
      segments.push(this.bytes.subarray(this.offset + 4, this.offset + 4 + following.length));
      this.offset += 4 + following.length;
      this.count();
    }
    return { type, segments };
  }

  private count() {
    this.budget.records += 1;
    if (this.budget.records > this.limits.maxRecords) {
      refuse('XLS_TOO_MANY_RECORDS', 'The workbook holds more records than allowed');
    }
  }
}

/** Reads across a record and its `CONTINUE`s, as BIFF8 splits a string between them. */
class SegmentReader {
  private segment = 0;
  private position = 0;

  constructor(private readonly segments: readonly Uint8Array[]) {}

  private current(): Uint8Array {
    for (;;) {
      const data = this.segments[this.segment];
      if (data === undefined) return refuse('XLS_TRUNCATED', 'A record ends inside its data');
      if (this.position < data.length) return data;
      this.segment += 1;
      this.position = 0;
    }
  }

  atEnd(): boolean {
    for (let index = this.segment; index < this.segments.length; index += 1) {
      const data = this.segments[index] as Uint8Array;
      if ((index === this.segment ? this.position : 0) < data.length) return false;
    }
    return true;
  }

  u8(): number {
    const data = this.current();
    const value = data[this.position] as number;
    this.position += 1;
    return value;
  }

  u16(): number {
    return this.u8() | (this.u8() << 8);
  }

  u32(): number {
    return (this.u16() | (this.u16() << 16)) >>> 0;
  }

  skip(count: number) {
    let rest = count;
    while (rest > 0) {
      const data = this.current();
      const take = Math.min(rest, data.length - this.position);
      this.position += take;
      rest -= take;
    }
  }

  /**
   * `count` characters, compressed (one byte each) or not. A string split
   * between records starts each continuation with a fresh option byte, which
   * may change the width mid-string.
   */
  chars(count: number, startsHighByte: boolean, decodeByte: (byte: number) => string): string {
    let highByte = startsHighByte;
    const parts: string[] = [];
    let rest = count;
    for (;;) {
      const data = this.segments[this.segment];
      const remaining = data === undefined ? 0 : data.length - this.position;
      const width = highByte ? 2 : 1;
      const take = Math.min(rest, Math.floor(remaining / width));
      if (data !== undefined && take > 0) {
        const codes: number[] = [];
        for (let index = 0; index < take; index += 1) {
          const at = this.position + index * width;
          codes.push(
            highByte
              ? (data[at] as number) | ((data[at + 1] as number) << 8)
              : (data[at] as number),
          );
        }
        parts.push(
          highByte ? fromCharCodes(codes) : codes.map((code) => decodeByte(code)).join(''),
        );
        this.position += take * width;
        rest -= take;
      }
      if (rest === 0) return parts.join('');
      if (remaining !== take * width) {
        refuse('XLS_MALFORMED', 'A character is split between records');
      }
      this.segment += 1;
      this.position = 0;
      if (this.segment >= this.segments.length) {
        refuse('XLS_TRUNCATED', 'A string ends before its length');
      }
      highByte = (this.u8() & 0x01) === 1;
    }
  }
}

function fromCharCodes(codes: readonly number[]): string {
  let text = '';
  for (let index = 0; index < codes.length; index += 4096) {
    text += String.fromCharCode(...codes.slice(index, index + 4096));
  }
  return text;
}

type StringContext = {
  readonly limits: XlsLimits;
  readonly budget: Budget;
  readonly decodeByte: (byte: number) => string;
};

function readUnicodeString(reader: SegmentReader, length: number, context: StringContext): string {
  const options = reader.u8();
  const runs = (options & 0x08) !== 0 ? reader.u16() : 0;
  const extension = (options & 0x04) !== 0 ? reader.u32() : 0;
  if (length > context.limits.maxStringChars) {
    refuse('XLS_STRING_TOO_LONG', 'A string is longer than a cell may hold');
  }
  context.budget.stringChars += length;
  if (context.budget.stringChars > context.limits.maxTotalStringChars) {
    refuse('XLS_TOO_MANY_STRINGS', 'The workbook holds more text than allowed');
  }
  const text = reader.chars(length, (options & 0x01) === 1, context.decodeByte);
  reader.skip(runs * 4);
  reader.skip(extension);
  return text;
}

function latin1(byte: number): string {
  return String.fromCharCode(byte);
}

const WINDOWS_1254_BYTES = Array.from({ length: 256 }, (_, byte) =>
  decodeWindows1254(Uint8Array.of(byte)),
);

/**
 * Compressed BIFF8 text is the low byte of UTF-16, so Latin-1, and Excel
 * always writes code page 1200. A writer that states 1254 has written its
 * compressed text in Windows-1254 instead — the only way the Turkish letters
 * outside Latin-1 fit in one byte — so it is decoded the way the file says it was written.
 */
function byteDecoderFor(codePage: number | null): (byte: number) => string {
  return codePage === CODEPAGE_WINDOWS_1254 ? (byte) => WINDOWS_1254_BYTES[byte] as string : latin1;
}

type Globals = {
  readonly sharedStrings: readonly string[];
  readonly dateStyles: ReadonlySet<number>;
  readonly uses1904: boolean;
  readonly firstWorksheetOffset: number;
  readonly decodeByte: (byte: number) => string;
};

function readBof(record: LogicalRecord, expectedType: number, what: string) {
  if (record.type !== RECORD.bof)
    refuse('XLS_NOT_A_WORKBOOK', `The ${what} does not begin a substream`);
  const data = record.segments[0] as Uint8Array;
  if (data.length < 4) refuse('XLS_TRUNCATED', 'A substream header is cut short');
  const version = (data[0] as number) | ((data[1] as number) << 8);
  const type = (data[2] as number) | ((data[3] as number) << 8);
  if (version !== BIFF8)
    refuse('XLS_VERSION_UNSUPPORTED', 'The workbook is not Excel 97–2003 (BIFF8)');
  if (type !== expectedType) refuse('XLS_NOT_A_WORKBOOK', `The ${what} is not what it should be`);
}

function u16At(data: Uint8Array, offset: number): number {
  if (offset + 2 > data.length) refuse('XLS_TRUNCATED', 'A record is shorter than its fields');
  return (data[offset] as number) | ((data[offset + 1] as number) << 8);
}

function u32At(data: Uint8Array, offset: number): number {
  return (u16At(data, offset) | (u16At(data, offset + 2) << 16)) >>> 0;
}

function readGlobals(stream: Uint8Array, limits: XlsLimits, budget: Budget): Globals {
  const records = new RecordStream(stream, 0, limits, budget);
  readBof(records.next(), 0x0005, 'workbook');

  let codePage: number | null = null;
  let uses1904 = false;
  const customFormats = new Map<number, string>();
  const styleFormats: number[] = [];
  const sheets: { offset: number; type: number }[] = [];
  let sharedStringRecord: LogicalRecord | null = null;
  const formatContext = (): StringContext => ({
    limits,
    budget,
    decodeByte: byteDecoderFor(codePage),
  });

  for (;;) {
    const record = records.next();
    const data = record.segments[0] as Uint8Array;
    if (record.type === RECORD.eof) break;
    switch (record.type) {
      case RECORD.filePass:
        return refuse('XLS_ENCRYPTED', 'The workbook is protected with a password');
      case RECORD.codePage:
        codePage = u16At(data, 0);
        break;
      case RECORD.dateMode:
        uses1904 = u16At(data, 0) === 1;
        break;
      case RECORD.format: {
        if (customFormats.size >= limits.maxFormats)
          refuse('XLS_MALFORMED', 'Too many number formats');
        const reader = new SegmentReader(record.segments);
        const index = reader.u16();
        customFormats.set(index, readUnicodeString(reader, reader.u16(), formatContext()));
        break;
      }
      case RECORD.xf:
        if (styleFormats.length >= limits.maxStyles)
          refuse('XLS_MALFORMED', 'Too many cell styles');
        styleFormats.push(u16At(data, 2));
        break;
      case RECORD.boundSheet:
        if (sheets.length >= limits.maxSheets) refuse('XLS_MALFORMED', 'Too many sheets');
        if (data.length < 6) refuse('XLS_TRUNCATED', 'A sheet entry is cut short');
        sheets.push({ offset: u32At(data, 0), type: data[5] as number });
        break;
      case RECORD.sst:
        sharedStringRecord = record;
        break;
      default:
        break;
    }
  }

  const decodeByte = byteDecoderFor(codePage);
  const sharedStrings =
    sharedStringRecord === null
      ? []
      : readSharedStrings(sharedStringRecord, { limits, budget, decodeByte });
  const dateStyles = new Set<number>();
  styleFormats.forEach((formatId, index) => {
    const code = customFormats.get(formatId);
    if (code !== undefined ? isDateFormatCode(code) : BUILT_IN_DATE_FORMATS.has(formatId)) {
      dateStyles.add(index);
    }
  });
  const worksheet = sheets.find((sheet) => sheet.type === 0);
  if (worksheet === undefined) refuse('XLS_NOT_A_WORKBOOK', 'The workbook has no worksheet');
  const firstWorksheetOffset = (worksheet as { offset: number }).offset;
  if (firstWorksheetOffset === 0 || firstWorksheetOffset >= stream.length) {
    refuse('XLS_MALFORMED', 'The first sheet is outside the workbook');
  }
  return { sharedStrings, dateStyles, uses1904, firstWorksheetOffset, decodeByte };
}

function readSharedStrings(record: LogicalRecord, context: StringContext): string[] {
  const reader = new SegmentReader(record.segments);
  reader.u32();
  const declared = reader.u32();
  const strings: string[] = [];
  while (strings.length < declared && !reader.atEnd()) {
    if (strings.length >= context.limits.maxSharedStrings) {
      refuse('XLS_TOO_MANY_STRINGS', 'The workbook has more shared strings than allowed');
    }
    strings.push(readUnicodeString(reader, reader.u16(), context));
  }
  return strings;
}

const ERROR_TEXT: Readonly<Record<number, string>> = {
  0: '#NULL!',
  7: '#DIV/0!',
  15: '#VALUE!',
  23: '#REF!',
  29: '#NAME?',
  36: '#NUM!',
  42: '#N/A',
};

/**
 * A stored double as the decimal text an `.xlsx` would have held for it —
 * seventeen significant digits — so `renderNumber` rounds both containers'
 * numbers to Excel's fifteen the same way.
 */
function doubleText(value: number): string {
  if (!Number.isFinite(value)) refuse('XLS_MALFORMED', 'A number cell is not a number');
  return value.toPrecision(17);
}

/** An RK value: a 30-bit integer or the top of a double, optionally hundredths. */
export function rkText(rk: number): string {
  const hundredths = (rk & 0x01) !== 0;
  if ((rk & 0x02) !== 0) {
    const integer = rk >> 2;
    if (!hundredths) return integer.toString();
    const sign = integer < 0 ? '-' : '';
    const digits = Math.abs(integer).toString().padStart(3, '0');
    return `${sign}${digits.slice(0, -2)}.${digits.slice(-2)}`;
  }
  const view = new DataView(new ArrayBuffer(8));
  view.setUint32(4, (rk & 0xfffffffc) >>> 0, true);
  const value = view.getFloat64(0, true);
  return doubleText(hundredths ? value / 100 : value);
}

type SheetContext = {
  readonly globals: Globals;
  readonly decimalSeparator: ',' | '.';
  readonly maxRows: number;
  readonly limits: XlsLimits;
  readonly budget: Budget;
};

function readSheetRows(stream: Uint8Array, context: SheetContext): string[][] {
  const { globals, limits } = context;
  const records = new RecordStream(stream, globals.firstWorksheetOffset, limits, context.budget);
  readBof(records.next(), 0x0010, 'first sheet');
  const stringContext: StringContext = {
    limits,
    budget: context.budget,
    decodeByte: globals.decodeByte,
  };

  const rows = new Map<number, string[]>();
  let lastRow = -1;
  let cells = 0;
  let cellChars = 0;
  let pendingFormulaString: { row: number; column: number } | null = null;

  const place = (row: number, column: number, text: string) => {
    if (row + 1 > context.maxRows)
      refuse('XLS_TOO_MANY_ROWS', 'The sheet has more rows than allowed');
    lastRow = Math.max(lastRow, row);
    if (column >= XLSX_MAX_COLUMNS || text === '') return;
    let cellsOfRow = rows.get(row);
    if (cellsOfRow === undefined) {
      cellsOfRow = [];
      rows.set(row, cellsOfRow);
    }
    cells += 1 + Math.max(0, column - cellsOfRow.length);
    if (cells > limits.maxCells) {
      refuse('XLS_TOO_MANY_CELLS', 'The sheet has more cells than allowed');
    }
    cellChars += text.length;
    if (cellChars > limits.maxCellChars) {
      refuse('XLS_TOO_MUCH_TEXT', 'The sheet holds more text than allowed');
    }
    while (cellsOfRow.length < column) cellsOfRow.push('');
    cellsOfRow[column] = text;
  };

  const numeric = (raw: string, style: number): string => {
    if (globals.dateStyles.has(style)) {
      const plain = renderNumber(raw, '.');
      const date = plain === null ? null : excelSerialToText(plain, globals.uses1904);
      if (date !== null) return date;
    }
    return renderNumber(raw, context.decimalSeparator) ?? raw;
  };

  const sharedString = (index: number): string => {
    const text = globals.sharedStrings[index];
    if (text === undefined) refuse('XLS_MALFORMED', 'A cell names a missing shared string');
    return text as string;
  };

  let depth = 0;
  for (;;) {
    const record = records.next();
    const data = record.segments[0] as Uint8Array;
    if (record.type === RECORD.bof) {
      depth += 1;
      continue;
    }
    if (record.type === RECORD.eof) {
      if (depth === 0) break;
      depth -= 1;
      continue;
    }
    if (depth > 0) continue;

    switch (record.type) {
      case RECORD.labelSst:
        pendingFormulaString = null;
        place(u16At(data, 0), u16At(data, 2), sharedString(u32At(data, 6)));
        break;
      case RECORD.label:
      case RECORD.rString: {
        pendingFormulaString = null;
        const reader = new SegmentReader(record.segments);
        const row = reader.u16();
        const column = reader.u16();
        reader.u16();
        place(row, column, readUnicodeString(reader, reader.u16(), stringContext));
        break;
      }
      case RECORD.number: {
        pendingFormulaString = null;
        if (data.length < 14) refuse('XLS_TRUNCATED', 'A number cell is cut short');
        const value = new DataView(data.buffer, data.byteOffset, data.byteLength).getFloat64(
          6,
          true,
        );
        place(u16At(data, 0), u16At(data, 2), numeric(doubleText(value), u16At(data, 4)));
        break;
      }
      case RECORD.rk:
        pendingFormulaString = null;
        place(u16At(data, 0), u16At(data, 2), numeric(rkText(u32At(data, 6)), u16At(data, 4)));
        break;
      case RECORD.mulRk: {
        pendingFormulaString = null;
        if (data.length < 6 || (data.length - 6) % 6 !== 0) {
          refuse('XLS_MALFORMED', 'A run of number cells has a broken length');
        }
        const row = u16At(data, 0);
        const firstColumn = u16At(data, 2);
        const count = (data.length - 6) / 6;
        if (u16At(data, data.length - 2) !== firstColumn + count - 1) {
          refuse('XLS_MALFORMED', 'A run of number cells does not end where it says');
        }
        for (let index = 0; index < count; index += 1) {
          const at = 4 + index * 6;
          place(row, firstColumn + index, numeric(rkText(u32At(data, at + 2)), u16At(data, at)));
        }
        break;
      }
      case RECORD.formula: {
        pendingFormulaString = null;
        if (data.length < 20) refuse('XLS_TRUNCATED', 'A formula cell is cut short');
        const row = u16At(data, 0);
        const column = u16At(data, 2);
        if (data[12] === 0xff && data[13] === 0xff) {
          const kind = data[6];
          if (kind === 0x00) pendingFormulaString = { row, column };
          else if (kind === 0x01) place(row, column, data[8] === 1 ? 'TRUE' : 'FALSE');
          else if (kind === 0x02) place(row, column, ERROR_TEXT[data[8] as number] ?? '#N/A');
          else place(row, column, '');
        } else {
          const value = new DataView(data.buffer, data.byteOffset, data.byteLength).getFloat64(
            6,
            true,
          );
          place(row, column, numeric(doubleText(value), u16At(data, 4)));
        }
        break;
      }
      case RECORD.string: {
        if (pendingFormulaString === null) break;
        const reader = new SegmentReader(record.segments);
        const text = readUnicodeString(reader, reader.u16(), stringContext);
        place(pendingFormulaString.row, pendingFormulaString.column, text);
        pendingFormulaString = null;
        break;
      }
      case RECORD.boolErr: {
        pendingFormulaString = null;
        if (data.length < 8) refuse('XLS_TRUNCATED', 'A boolean cell is cut short');
        const value = data[6] as number;
        const text = data[7] === 1 ? (ERROR_TEXT[value] ?? '#N/A') : value === 1 ? 'TRUE' : 'FALSE';
        place(u16At(data, 0), u16At(data, 2), text);
        break;
      }
      case RECORD.blank:
        place(u16At(data, 0), u16At(data, 2), '');
        break;
      case RECORD.mulBlank:
        place(u16At(data, 0), u16At(data, 2), '');
        break;
      default:
        break;
    }
  }

  const out: string[][] = [];
  for (let row = 0; row <= lastRow; row += 1) out.push(rows.get(row) ?? []);
  return out;
}

/**
 * Reads the first worksheet of a BIFF8 workbook. Throws `XlsRefusedError` for
 * anything it will not read; never returns part of a sheet.
 */
export function readXlsFirstSheet(bytes: Uint8Array, options: XlsReadOptions): XlsSheet {
  const limits = options.limits ?? XLS_LIMITS;
  const file = openCompoundFile(bytes, limits);
  if (
    named(file.streams, 'EncryptedPackage').length > 0 ||
    named(file.streams, 'EncryptionInfo').length > 0
  ) {
    refuse('XLS_ENCRYPTED', 'The workbook is protected with a password');
  }
  const workbooks = named(file.streams, 'Workbook');
  if (workbooks.length === 0) {
    if (named(file.streams, 'Book').length > 0) {
      refuse('XLS_VERSION_UNSUPPORTED', 'The workbook is older than Excel 97 (BIFF5)');
    }
    refuse('XLS_NOT_A_WORKBOOK', 'The compound file holds no workbook');
  }
  if (workbooks.length > 1) refuse('XLS_MALFORMED', 'The compound file holds two workbooks');
  const stream = file.read(workbooks[0] as CompoundStream);
  const budget: Budget = { records: 0, stringChars: 0 };
  const globals = readGlobals(stream, limits, budget);
  return {
    rows: readSheetRows(stream, {
      globals,
      decimalSeparator: options.decimalSeparator,
      maxRows: options.maxRows,
      limits,
      budget,
    }),
  };
}
